<script setup>
import {
  ref,
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  useId,
  watch,
} from 'vue';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  getTopmostDashboardModalLayer,
  markDashboardModalEscapeHandled,
  registerDashboardModalLayer,
  unregisterDashboardModalLayer,
  wasDashboardModalEscapeHandled,
} from 'dashboard/components/dashboardModalStack';

const props = defineProps({
  closeOnBackdropClick: { type: Boolean, default: true },
  closeOnEscape: { type: Boolean, default: true },
  showCloseButton: { type: Boolean, default: true },
  // Keep legacy :on-close from falling through to the dialog DOM. Its raw
  // kebab-case handler is invoked once alongside the modern close event below.
  // eslint-disable-next-line vue/no-unused-properties
  onClose: { type: Function, default: null },
  beforeClose: { type: Function, default: null },
  fullWidth: { type: Boolean, default: false },
  modalType: { type: String, default: 'centered' },
  size: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  ariaLabelledby: { type: String, default: '' },
  initialFocusSelector: { type: String, default: '' },
  teleportTo: { type: [String, Object], default: '' },
});

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });
defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const instance = getCurrentInstance();
const modalId = `dashboard-modal-${useId()}`;
const modalMaskRef = ref(null);
const modalContainerRef = ref(null);
const detectedTitleId = ref('');
const forceTeleportToBody = ref(false);
const mousedDownOnBackdrop = ref(false);
const hasBodyScrollLock = ref(false);
const isClosePending = ref(false);
const isTopmostInStack = ref(false);
const modalStackEntry = {
  containerRef: modalContainerRef,
  isTopmost: isTopmostInStack,
  maskRef: modalMaskRef,
};

let previouslyFocusedElement = null;
let previousFocusFallbackElement = null;
let visibilityGeneration = 0;
let closeAttemptGeneration = 0;
let isUnmounted = false;
let titleObserver = null;

const FOCUSABLE_ELEMENT_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'details > summary:first-of-type',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const PROGRAMMATIC_FOCUS_ELEMENT_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'details > summary:first-of-type',
  '[contenteditable="true"]',
  '[tabindex]',
].join(',');

const TITLE_ELEMENT_SELECTOR = [
  '[data-modal-title]',
  '[data-test-id="modal-header-title"]',
  '[role="heading"]',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
].join(',');

const labelledBy = computed(() => {
  if (props.ariaLabel) return undefined;
  return props.ariaLabelledby || detectedTitleId.value || undefined;
});

const isSidePanel = computed(() =>
  ['left-aligned', 'right-aligned'].includes(props.modalType)
);

const modalAlignmentClassName = computed(() => {
  if (props.modalType === 'left-aligned') {
    return 'left-aligned justify-start';
  }

  if (props.modalType === 'right-aligned') {
    return 'right-aligned justify-end';
  }

  return 'justify-center';
});

const modalLayoutClassName = computed(() => {
  if (isSidePanel.value) {
    return 'rounded-none h-full w-[30rem] max-w-full';
  }

  if (props.fullWidth) {
    return 'items-center rounded-none flex h-full justify-center w-full';
  }

  return 'rounded-xl max-w-[37.5rem] w-full mx-4';
});

const modalSizeClassName = computed(() => {
  const normalizedSize = props.size ?? '';

  const modalSizeClassNameMap = {
    tiny: '!max-w-sm',
    small: '!max-w-lg',
    medium: '!max-w-[80%] !w-[56.25rem]',
    large: '!max-w-[90%] !w-[72rem] !h-[85vh]',
    full: '!max-w-[95%] !w-[90rem] !h-[90vh]',
    'full-width':
      '!mx-4 !max-w-[calc(100vw-2rem)] !w-[calc(100vw-2rem)] !h-[calc(100dvh-2rem)]',
    'modal-big': '!w-full',
  };

  // A few callers pass Tailwind utility overrides directly through `size`.
  return normalizedSize
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(sizeToken => modalSizeClassNameMap[sizeToken] || sizeToken)
    .join(' ');
});

const isTeleportDisabled = computed(
  () => !props.teleportTo && !forceTeleportToBody.value
);

const isTopmostModal = () => {
  return isTopmostInStack.value;
};

const registerOpenModal = () => {
  registerDashboardModalLayer(modalStackEntry);
};

const unregisterOpenModal = () => {
  unregisterDashboardModalLayer(modalStackEntry);
};

const isProgrammaticallyFocusableElementVisible = element => {
  if (!element?.matches?.(PROGRAMMATIC_FOCUS_ELEMENT_SELECTOR)) return false;
  if (element.matches(':disabled')) return false;
  if (element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;

  let currentElement = element;
  while (currentElement) {
    const styles = window.getComputedStyle(currentElement);
    if (
      styles.display === 'none' ||
      styles.visibility === 'hidden' ||
      styles.visibility === 'collapse' ||
      styles.contentVisibility === 'hidden'
    ) {
      return false;
    }
    currentElement = currentElement.parentElement;
  }

  // JSDOM does not implement layout and reports no client rects for every
  // element. In a browser, an empty rect list reliably excludes controls
  // clipped from layout by a hidden ancestor (including closed disclosure UI).
  const browserHasLayout = document.documentElement.getClientRects().length > 0;
  return !browserHasLayout || element.getClientRects().length > 0;
};

const isFocusableElementVisible = element =>
  element.tabIndex >= 0 && isProgrammaticallyFocusableElementVisible(element);

const getFocusableElements = (container = modalContainerRef.value) => {
  if (!container) return [];

  const candidates = Array.from(
    container.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR)
  ).filter(isFocusableElementVisible);

  const radioGroups = new Map();
  candidates.forEach(element => {
    if (!(element instanceof HTMLInputElement)) return;
    if (element.type !== 'radio' || !element.name) return;

    const owner = element.form || element.getRootNode();
    if (!radioGroups.has(owner)) radioGroups.set(owner, new Map());
    const ownerGroups = radioGroups.get(owner);
    const group = ownerGroups.get(element.name) || [];
    group.push(element);
    ownerGroups.set(element.name, group);
  });

  const tabbableRadioButtons = new Set();
  radioGroups.forEach(ownerGroups => {
    ownerGroups.forEach(group => {
      tabbableRadioButtons.add(
        group.find(element => element.checked) || group[0]
      );
    });
  });

  return candidates
    .filter(element => {
      if (!(element instanceof HTMLInputElement)) return true;
      if (element.type !== 'radio' || !element.name) return true;
      return tabbableRadioButtons.has(element);
    })
    .map((element, domOrder) => ({ domOrder, element }))
    .sort((first, second) => {
      const firstTabIndex = first.element.tabIndex;
      const secondTabIndex = second.element.tabIndex;
      const firstHasPriority = firstTabIndex > 0;
      const secondHasPriority = secondTabIndex > 0;

      if (firstHasPriority && secondHasPriority) {
        return (
          firstTabIndex - secondTabIndex || first.domOrder - second.domOrder
        );
      }
      if (firstHasPriority) return -1;
      if (secondHasPriority) return 1;
      return first.domOrder - second.domOrder;
    })
    .map(({ element }) => element);
};

const focusElement = element => {
  element?.focus({ preventScroll: true });
};

const findPopupTrigger = element => {
  const popup = element?.closest?.('[role="menu"][id], [role="listbox"][id]');
  if (!popup?.id) return null;

  return Array.from(document.querySelectorAll('[aria-controls]')).find(
    candidate =>
      candidate.getAttribute('aria-controls')?.split(/\s+/).includes(popup.id)
  );
};

const isElementInModalFocusScope = (element, container) => {
  if (!element || !container) return false;
  if (container.contains(element)) return true;

  const popupTrigger = findPopupTrigger(element);
  return Boolean(popupTrigger && container.contains(popupTrigger));
};

const findInitialFocusElement = () => {
  if (!modalContainerRef.value) return null;

  if (props.initialFocusSelector) {
    try {
      const requestedElement = modalContainerRef.value.querySelector(
        props.initialFocusSelector
      );
      if (
        requestedElement &&
        isProgrammaticallyFocusableElementVisible(requestedElement)
      ) {
        return requestedElement;
      }
    } catch {
      // Invalid selectors fall back to the first available control.
    }
  }

  const autofocusElement = modalContainerRef.value.querySelector('[autofocus]');
  if (
    autofocusElement &&
    isProgrammaticallyFocusableElementVisible(autofocusElement)
  ) {
    return autofocusElement;
  }

  const focusableElements = getFocusableElements();
  return (
    focusableElements.find(
      element => !element.hasAttribute('data-modal-close')
    ) ||
    focusableElements[0] ||
    modalContainerRef.value
  );
};

const syncAccessibleTitle = () => {
  if (props.ariaLabel || props.ariaLabelledby || !modalContainerRef.value) {
    detectedTitleId.value = '';
    return;
  }

  const titleElement = Array.from(
    modalContainerRef.value.querySelectorAll(TITLE_ELEMENT_SELECTOR)
  ).find(
    element =>
      element.closest('[data-dashboard-modal="true"]') === modalMaskRef.value
  );

  if (!titleElement) {
    detectedTitleId.value = '';
    return;
  }

  if (!titleElement.id) titleElement.id = `${modalId}-title`;
  detectedTitleId.value = titleElement.id;
};

const stopTitleObserver = () => {
  titleObserver?.disconnect();
  titleObserver = null;
};

const startTitleObserver = () => {
  stopTitleObserver();
  if (!modalContainerRef.value || typeof MutationObserver === 'undefined') {
    return;
  }

  titleObserver = new MutationObserver(() => {
    if (!isUnmounted && show.value) syncAccessibleTitle();
  });
  titleObserver.observe(modalContainerRef.value, {
    childList: true,
    subtree: true,
  });
};

const lockBodyScroll = () => {
  if (hasBodyScrollLock.value || !document.body) return;

  const { body } = document;
  const lockCount = Number.parseInt(
    body.dataset.dashboardModalLockCount || '0',
    10
  );

  if (lockCount === 0) {
    body.dataset.dashboardModalPreviousOverflow = body.style.overflow;
  }

  body.dataset.dashboardModalLockCount = String(lockCount + 1);
  body.style.overflow = 'hidden';
  hasBodyScrollLock.value = true;
};

const unlockBodyScroll = () => {
  if (!hasBodyScrollLock.value || !document.body) return;

  const { body } = document;
  const lockCount = Math.max(
    0,
    Number.parseInt(body.dataset.dashboardModalLockCount || '1', 10) - 1
  );

  if (lockCount === 0) {
    body.style.overflow = body.dataset.dashboardModalPreviousOverflow || '';
    delete body.dataset.dashboardModalPreviousOverflow;
    delete body.dataset.dashboardModalLockCount;
  } else {
    body.dataset.dashboardModalLockCount = String(lockCount);
  }

  hasBodyScrollLock.value = false;
};

const restorePreviousFocus = () => {
  const topmostEntry = getTopmostDashboardModalLayer();
  if (topmostEntry) {
    const topmostContainer = topmostEntry.containerRef.value;
    const activeElement = document.activeElement;

    if (
      isElementInModalFocusScope(activeElement, topmostContainer) &&
      isProgrammaticallyFocusableElementVisible(activeElement)
    ) {
      previouslyFocusedElement = null;
      previousFocusFallbackElement = null;
      return;
    }

    let focusTarget = previouslyFocusedElement;
    if (
      !isElementInModalFocusScope(focusTarget, topmostContainer) ||
      !isProgrammaticallyFocusableElementVisible(focusTarget)
    ) {
      const focusableElements = topmostContainer
        ? getFocusableElements(topmostContainer)
        : [];
      focusTarget =
        focusableElements.find(
          element => !element.hasAttribute('data-modal-close')
        ) ||
        focusableElements[0] ||
        topmostContainer;
    }

    focusElement(focusTarget);
    previouslyFocusedElement = null;
    previousFocusFallbackElement = null;
    return;
  }

  let focusTarget = previouslyFocusedElement;
  if (
    !focusTarget?.isConnected ||
    !isProgrammaticallyFocusableElementVisible(focusTarget)
  ) {
    focusTarget = previousFocusFallbackElement;
  }
  if (
    !focusTarget?.isConnected ||
    !isProgrammaticallyFocusableElementVisible(focusTarget)
  ) {
    focusTarget = null;
  }

  focusElement(focusTarget);
  previouslyFocusedElement = null;
  previousFocusFallbackElement = null;
};

const shouldRestoreFocusAfterUnregister = () => {
  return (
    isTopmostModal() ||
    modalContainerRef.value?.contains(document.activeElement)
  );
};

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const invalidateCloseAttempt = () => {
  closeAttemptGeneration += 1;
  isClosePending.value = false;
};

const invokeKebabCaseCloseProp = () => {
  const legacyHandler = instance?.vnode.props?.['on-close'];
  if (typeof legacyHandler !== 'function') return;

  legacyHandler();
};

const close = async () => {
  if (isClosePending.value || !show.value) return;

  const openCycle = visibilityGeneration;
  closeAttemptGeneration += 1;
  const closeAttempt = closeAttemptGeneration;
  isClosePending.value = true;
  let canClose = true;

  try {
    canClose = props.beforeClose ? await props.beforeClose() : true;
  } catch {
    // A failed guard is a veto. Keep the modal open without leaking an
    // unhandled promise rejection from Escape, backdrop, or close-button use.
    canClose = false;
  }

  const isCurrentAttempt =
    !isUnmounted &&
    show.value &&
    openCycle === visibilityGeneration &&
    closeAttempt === closeAttemptGeneration;

  if (!isCurrentAttempt) return;

  if (canClose === false) {
    isClosePending.value = false;
    return;
  }

  // A controlled parent can reopen synchronously from @close. Complete this
  // attempt before invoking callbacks so the reopened dialog is dismissible.
  isClosePending.value = false;
  show.value = false;
  emit('close');
  invokeKebabCaseCloseProp();
};

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (props.closeOnBackdropClick && isTopmostModal()) {
      close();
    }
  }
};

const onKeydown = e => {
  if (!show.value || !isTopmostModal() || wasDashboardModalEscapeHandled(e)) {
    return;
  }

  if (e.key === 'Escape' || e.code === 'Escape') {
    const popupTrigger = findPopupTrigger(e.target);
    if (
      popupTrigger &&
      modalContainerRef.value?.contains(popupTrigger) &&
      !modalContainerRef.value.contains(e.target)
    ) {
      return;
    }

    // Keep the keystroke from reaching window-level shortcuts or drawers,
    // while allowing other listeners registered on document to react.
    e.stopPropagation();
    if (!props.closeOnEscape) return;

    // Mark this event for the other Modal instances before closing can
    // synchronously reveal another dialog. Do not stop unrelated listeners.
    markDashboardModalEscapeHandled(e);
    e.preventDefault();
    close();
    return;
  }

  if (e.key !== 'Tab') return;

  const focusableElements = getFocusableElements();
  if (!focusableElements.length) {
    e.preventDefault();
    focusElement(modalContainerRef.value);
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement;
  const isOutsideTabSequence = !focusableElements.includes(activeElement);

  if (
    e.shiftKey &&
    (isOutsideTabSequence ||
      activeElement === firstElement ||
      !modalContainerRef.value.contains(activeElement))
  ) {
    e.preventDefault();
    focusElement(lastElement);
  } else if (
    !e.shiftKey &&
    (isOutsideTabSequence ||
      activeElement === lastElement ||
      !modalContainerRef.value.contains(activeElement))
  ) {
    e.preventDefault();
    focusElement(firstElement);
  }
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);

watch(
  show,
  async (isVisible, _previousValue, onCleanup) => {
    visibilityGeneration += 1;
    const currentGeneration = visibilityGeneration;
    let isCancelled = false;

    invalidateCloseAttempt();
    onCleanup(() => {
      isCancelled = true;
    });

    const isCurrentCycle = () =>
      !isUnmounted &&
      !isCancelled &&
      currentGeneration === visibilityGeneration &&
      show.value === isVisible;

    if (isVisible) {
      previouslyFocusedElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      previousFocusFallbackElement = findPopupTrigger(previouslyFocusedElement);
      registerOpenModal();
      lockBodyScroll();

      await nextTick();
      if (!isCurrentCycle()) return;

      const parentModal = modalMaskRef.value?.parentElement?.closest(
        '[data-dashboard-modal="true"]'
      );
      if (!props.teleportTo && parentModal) {
        forceTeleportToBody.value = true;
        await nextTick();
        if (!isCurrentCycle()) return;
      }

      syncAccessibleTitle();
      startTitleObserver();
      await nextTick();
      if (!isCurrentCycle()) return;

      if (!isTopmostModal()) return;
      focusElement(findInitialFocusElement());
      return;
    }

    mousedDownOnBackdrop.value = false;
    detectedTitleId.value = '';
    stopTitleObserver();
    const shouldRestoreFocus = shouldRestoreFocusAfterUnregister();
    unregisterOpenModal();
    unlockBodyScroll();
    await nextTick();
    if (!isCurrentCycle()) return;
    if (!shouldRestoreFocus) {
      previouslyFocusedElement = null;
      previousFocusFallbackElement = null;
      return;
    }

    restorePreviousFocus();
  },
  { immediate: true, flush: 'sync' }
);

onBeforeUnmount(() => {
  const shouldRestoreFocus = shouldRestoreFocusAfterUnregister();
  isUnmounted = true;
  visibilityGeneration += 1;
  invalidateCloseAttempt();
  stopTitleObserver();
  unregisterOpenModal();
  unlockBodyScroll();
  if (shouldRestoreFocus) {
    // The newly promoted modal remains inert until Vue applies this DOM
    // update. Restore only after its interaction state is current.
    nextTick(() => restorePreviousFocus());
  } else {
    previouslyFocusedElement = null;
    previousFocusFallbackElement = null;
  }
});

// Note: onClose prop is deprecated in favor of @close event
// Both patterns work, but @close is preferred
</script>

<template>
  <Teleport :to="teleportTo || 'body'" :disabled="isTeleportDisabled">
    <transition
      enter-active-class="transition-opacity duration-150 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-100 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        ref="modalMaskRef"
        v-bind="$attrs"
        role="dialog"
        :aria-hidden="isTopmostInStack ? undefined : 'true'"
        :aria-modal="isTopmostInStack ? 'true' : undefined"
        :aria-label="ariaLabel || undefined"
        :aria-labelledby="labelledBy"
        :inert="isTopmostInStack ? undefined : ''"
        data-dashboard-modal="true"
        class="modal-mask skip-context-menu flex items-center bg-n-alpha-black2 backdrop-blur-[4px] h-full fixed inset-0 w-full overscroll-contain pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]"
        :class="[
          modalAlignmentClassName,
          isTopmostInStack ? 'z-[9991]' : 'z-[9990]',
        ]"
        @mousedown.self="handleMouseDown"
      >
        <div
          :id="modalId"
          ref="modalContainerRef"
          tabindex="-1"
          class="relative max-h-[calc(100dvh_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom))] overflow-auto overscroll-contain bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu [&_.content]:p-8 [&_form]:pt-4 [&_form]:pb-8 [&_form]:px-8 [&_form]:self-center [&_.modal-content]:pt-4 [&_.modal-content]:pb-8 [&_.modal-content]:px-8 [&_.modal-content]:self-center [&_form_a]:p-4 [&_.modal-content_a]:p-4"
          :class="[modalLayoutClassName, modalSizeClassName]"
          @mouse.stop
          @mousedown.stop
        >
          <slot />
          <Button
            v-if="showCloseButton"
            ghost
            slate
            lg
            type="button"
            icon="i-lucide-x"
            :aria-label="t('GENERAL.CLOSE')"
            :disabled="isClosePending"
            data-modal-close
            class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
            @click="close"
          />
        </div>
      </div>
    </transition>
  </Teleport>
</template>
